/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Content List Parameters
 */
export interface ContentListParams {
  organization_id: string;
  team_id?: string;
  content_types?: string[];
  status?: 'draft' | 'private' | 'public' | 'archived';
  search_query?: string;
  tag_filters?: string[];
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/**
 * Content List Response
 */
export interface ContentListResponse {
  content: any[];
  data?: any[];  // Alias for content (some consumers use .data)
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
    page_size: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/**
 * Content Update Data
 */
export interface ContentUpdateData {
  title?: string;
  description?: string;
  tags?: string[];
  personal_tags?: string[];
  status?: 'draft' | 'private' | 'public' | 'archived';
  metadata?: Record<string, any>;
  content_type?: string;
  team_id?: string;
}

/**
 * Content Stats Parameters
 */
export interface ContentStatsParams {
  organization_id: string;
  team_id?: string;
  time_range?: 'day' | 'week' | 'month' | 'year' | 'all';
  group_by?: 'content_type' | 'author' | 'status' | 'date';
}

/**
 * Content Streaming Metadata
 */
export interface ContentStreamingMetadata {
  contentId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  duration?: number;
  streamUrl?: string;
  chunkSize?: number;
  totalChunks?: number;
  supportedQualities?: string[];
  defaultQuality?: string;
  thumbnailUrl?: string;
  canStream: boolean;
  canDownload: boolean;
  requiresAuth: boolean;
  expiresAt?: string;
  metadata?: Record<string, string>; // Additional metadata including presigned URLs
}

/**
 * Content Rating Response
 */
export interface ContentRatingResponse {
  success: boolean;
  message: string;
  averageRating?: number;
  totalRatings?: number;
  userRating?: number;
}

/**
 * Content Service
 * 
 * Handles all content-related operations including:
 * - Content CRUD operations
 * - File management (upload, download, streaming)
 * - Content ratings and bookmarks
 * - Content conversion (PDF)
 * - Content metadata and statistics
 * 
 * Extracted from the monolithic API service.
 */
export class ContentService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  /**
   * List content with filtering and pagination
   */
  async listContent(params: ContentListParams): Promise<ContentListResponse> {
    try {
      console.log('Fetching content list with params:', params);
      
      const response = await this.get<ContentListResponse>('/content', {
        params,
        timeout: 10000
      });
      
      console.log(`Retrieved ${response.content?.length || 0} content items`);
      return response;
    } catch (error) {
      console.error('Error fetching content list:', error);
      throw this.errorHandler.handleError(error, 'ContentService.listContent');
    }
  }

  /**
   * Get single content item by ID
   * @param contentId - The ID of the content to fetch
   * @param teamId - The team ID for subscription validation (REQUIRED)
   */
  async getContent(contentId: string, teamId: string, fresh = false): Promise<any> {
    try {
      const url = `/content/${contentId}`;
      const params = { team_id: teamId };
      const response = fresh
        ? await this.get<any>(url, { params, timeout: 10000 })
        : await this.getCached<any>(url, params, { timeout: 10000 });
      return response;
    } catch (error) {
      console.error(`Error fetching content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getContent(${contentId})`);
    }
  }

  /**
   * Update content metadata
   */
  async updateContent(contentId: string, updateData: ContentUpdateData): Promise<{
    content: any;
    message: string;
  }> {
    try {
      console.log(`Updating content ${contentId}:`, updateData);

      const response = await this.put<any>(`/content/${contentId}`, updateData);

      // Invalidate content cache
      this.invalidateCache('content');

      // Invalidate team and organization caches to ensure statistics are updated
      // (content updates might affect metadata that's aggregated in team stats)
      this.invalidateCache('team');
      this.invalidateCache('organization');

      return {
        content: response,
        message: 'Content updated successfully'
      };
    } catch (error) {
      console.error(`Error updating content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.updateContent(${contentId})`);
    }
  }

  /**
   * Delete content
   */
  async deleteContent(contentId: string, teamId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      console.log(`Deleting content: ${contentId} (team: ${teamId})`);

      await this.delete(`/content/${contentId}`, {
        data: {
          team_id: teamId
        }
      });

      // Invalidate content cache
      this.invalidateCache('content');

      // Invalidate team cache to update content count and storage statistics
      this.invalidateCache('team');

      // Invalidate organization cache as well (team data is part of org data)
      this.invalidateCache('organization');

      return {
        success: true,
        message: 'Content deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.deleteContent(${contentId})`);
    }
  }

  /**
   * Apply tags to content
   */
  async applyTagsToContent(contentId: string, tagIds: string[], teamId: string): Promise<{
    success: boolean;
    message: string;
    tags?: any[];
  }> {
    try {
      console.log(`Applying ${tagIds.length} tags to content ${contentId} (team: ${teamId})`);

      const response = await this.post<any>(`/content/${contentId}/tags`, {
        tag_ids: tagIds,
        team_id: teamId
      });

      // Invalidate content cache
      this.invalidateCache('content');

      return {
        success: true,
        message: 'Tags applied successfully',
        tags: response.tags
      };
    } catch (error) {
      console.error(`Error applying tags to content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.applyTagsToContent(${contentId})`);
    }
  }

  /**
   * Remove tags from content
   */
  async removeTagsFromContent(contentId: string, tagIds: string[], teamId: string): Promise<{
    success: boolean;
    message: string;
    remainingTags?: any[];
  }> {
    try {
      console.log(`Removing ${tagIds.length} tags from content ${contentId} (team: ${teamId})`);

      const response = await this.delete<any>(`/content/${contentId}/tags`, {
        data: {
          tag_ids: tagIds,
          team_id: teamId
        }
      });

      // Invalidate content cache
      this.invalidateCache('content');

      return {
        success: true,
        message: 'Tags removed successfully',
        remainingTags: response.tags
      };
    } catch (error) {
      console.error(`Error removing tags from content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.removeTagsFromContent(${contentId})`);
    }
  }

  /**
   * Get content file URL
   */
  async getContentFileUrl(contentId: string): Promise<{
    fileUrl: string;
    fileName: string;
    fileType: string;
  }> {
    try {
      console.log(`Getting file URL for content: ${contentId}`);
      
      const response = await this.get<any>(`/content/${contentId}/file-url`);
      
      return {
        fileUrl: response.file_url || response.fileUrl,
        fileName: response.file_name || response.fileName,
        fileType: response.file_type || response.fileType || response.mime_type
      };
    } catch (error) {
      console.error(`Error getting file URL for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getContentFileUrl(${contentId})`);
    }
  }

  /**
   * View content file as blob (for in-app viewing, tracks as view)
   * Uses download endpoint without download flag - defaults to view tracking
   */
  async viewContentFile(contentId: string, teamId: string): Promise<Blob> {
    try {
      console.log(`Viewing content file: ${contentId}`);

      // Use download endpoint without download flag - defaults to view tracking
      const response = await this.httpClient.get(`/content/${contentId}/download`, {
        params: {
          team_id: teamId // Required for subscription validation
        },
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for viewing
      });

      return response.data;
    } catch (error) {
      console.error(`Error viewing content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.viewContentFile(${contentId})`);
    }
  }

  /**
   * Download content file as blob (for user downloads, tracks as download)
   * Uses download endpoint with download=true flag to trigger download tracking
   */
  async downloadContentFile(contentId: string, teamId: string): Promise<Blob> {
    try {
      console.log(`Downloading content file: ${contentId}`);

      // Use download endpoint with download flag to properly track downloads
      const response = await this.httpClient.get(`/content/${contentId}/download`, {
        params: {
          download: 'true', // Set download flag to track as download
          team_id: teamId // Required for subscription validation
        },
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for downloads
      });

      return response.data;
    } catch (error) {
      console.error(`Error downloading content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.downloadContentFile(${contentId})`);
    }
  }

  /**
   * Download content thumbnail
   * @param contentId - The ID of the content
   * @param teamId - The team ID for subscription validation (REQUIRED by the gateway)
   */
  async downloadContentThumbnail(contentId: string, teamId?: string): Promise<Blob> {
    try {
      console.log(`Downloading thumbnail for content: ${contentId}`);

      const response = await this.httpClient.get(`/content/${contentId}/thumbnail`, {
        params: teamId ? { team_id: teamId } : undefined,
        responseType: 'blob',
        timeout: 30000
      });

      return response.data;
    } catch (error) {
      console.error(`Error downloading thumbnail for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.downloadContentThumbnail(${contentId})`);
    }
  }

  /**
   * Phase 2.5: Get presigned download URL for direct R2 access
   * This bypasses Gateway/Content Service streaming for better performance
   */
  async getPresignedDownloadUrl(contentId: string): Promise<{url: string, fileName?: string, contentType?: string}> {
    try {
      console.log(`Getting presigned download URL for content: ${contentId}`);
      
      const response = await this.httpClient.get(`/content/${contentId}/presigned-download-url`);

      // The API response structure is { data: { presigned_url, file_name, mime_type, ... } }
      console.log('Presigned URL response:', response.data);

      return {
        url: response.data.data.presigned_url,
        fileName: response.data.data.file_name,
        contentType: response.data.data.mime_type,
      };
    } catch (error) {
      console.error(`Error getting presigned download URL for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getPresignedDownloadUrl(${contentId})`);
    }
  }

  /**
   * Phase 2.5: Download file using direct R2 access (presigned URL)
   * This provides better performance by bypassing Gateway streaming
   */
  async downloadContentFileDirect(contentId: string): Promise<Blob> {
    try {
      console.log(`Downloading content file directly from R2: ${contentId}`);
      
      // Get presigned URL from Gateway
      const { url } = await this.getPresignedDownloadUrl(contentId);
      
      // Download directly from R2
      const response = await this.httpClient.get(url, {
        responseType: 'blob',
        timeout: 60000, // 60 second timeout
        // No authorization needed - presigned URL includes temporary credentials
        headers: {}
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error downloading content file directly ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.downloadContentFileDirect(${contentId})`);
    }
  }

  /**
   * Get content streaming metadata
   */
  async getContentStreamingMetadata(contentId: string, teamId?: string): Promise<ContentStreamingMetadata> {
    try {
      console.log(`Getting streaming metadata for content: ${contentId}`, { teamId });

      const params = teamId ? { team_id: teamId } : undefined;
      const response = await this.getCached<any>(
        `/content/${contentId}/metadata`,
        params
      );
      
      // Normalize the response and include metadata map
      return {
        contentId: response.content_id || response.contentId || contentId,
        fileName: response.file_name || response.fileName,
        fileSize: response.file_size || response.fileSize,
        mimeType: response.mime_type || response.mimeType,
        duration: response.duration,
        streamUrl: response.stream_url || response.streamUrl,
        chunkSize: response.chunk_size || response.chunkSize,
        totalChunks: response.total_chunks || response.totalChunks,
        supportedQualities: response.supported_qualities || response.supportedQualities,
        defaultQuality: response.default_quality || response.defaultQuality,
        thumbnailUrl: response.thumbnail_url || response.thumbnailUrl,
        canStream: response.can_stream !== undefined ? response.can_stream : response.canStream,
        canDownload: response.can_download !== undefined ? response.can_download : response.canDownload,
        requiresAuth: response.requires_auth !== undefined ? response.requires_auth : response.requiresAuth,
        expiresAt: response.expires_at || response.expiresAt,
        // Include the metadata map from the response
        metadata: response.metadata || {}
      };
    } catch (error) {
      console.error(`Error getting streaming metadata for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getContentStreamingMetadata(${contentId})`);
    }
  }

  /**
   * Stream content file with range support
   */
  async streamContentFile(
    contentId: string,
    range?: { start: number; end: number },
    quality?: string
  ): Promise<{
    data: ReadableStream | Blob;
    contentType: string;
    contentLength: number;
    acceptRanges?: string;
    contentRange?: string;
  }> {
    try {
      console.log(`Streaming content file: ${contentId}`, { range, quality });
      
      const headers: any = {};
      if (range) {
        headers.Range = `bytes=${range.start}-${range.end}`;
      }
      
      const params: any = {};
      if (quality) {
        params.quality = quality;
      }
      
      const response = await this.httpClient.get(`/content/${contentId}/stream`, {
        headers,
        params,
        responseType: 'blob',
        timeout: 120000 // 2 minute timeout for streaming
      });
      
      return {
        data: response.data,
        contentType: response.headers['content-type'],
        contentLength: parseInt(response.headers['content-length'] || '0'),
        acceptRanges: response.headers['accept-ranges'],
        contentRange: response.headers['content-range']
      };
    } catch (error) {
      console.error(`Error streaming content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.streamContentFile(${contentId})`);
    }
  }

  /**
   * Get content statistics
   */
  async getContentStats(params: ContentStatsParams): Promise<any> {
    try {
      console.log('Getting content statistics:', params);
      
      const response = await this.getCached<any>(
        '/content/stats',
        params
      );
      
      return response;
    } catch (error) {
      console.error('Error getting content stats:', error);
      throw this.errorHandler.handleError(error, 'ContentService.getContentStats');
    }
  }

  /**
   * Check if content is bookmarked by current user
   */
  async isContentBookmarked(contentId: string): Promise<{
    is_bookmarked: boolean;
    bookmark_id?: string;
  }> {
    try {
      const response = await this.get<any>(`/content/${contentId}/bookmarked`);
      
      return {
        is_bookmarked: response.is_bookmarked || false,
        bookmark_id: response.bookmark_id
      };
    } catch (error) {
      console.error(`Error checking bookmark for content ${contentId}:`, error);
      return { is_bookmarked: false };
    }
  }

  /**
   * Batch check bookmark status for multiple content items
   */
  async batchCheckBookmarks(contentIds: string[]): Promise<{
    bookmarked: { [contentId: string]: boolean };
    bookmark_ids: { [contentId: string]: string };
  }> {
    try {
      const response = await this.post<any>('/content/bookmarks/batch-check', {
        content_ids: contentIds
      });
      
      return {
        bookmarked: response.bookmarked || {},
        bookmark_ids: response.bookmark_ids || {}
      };
    } catch (error) {
      console.error('Error batch checking bookmarks:', error);
      // Return empty maps on error
      return {
        bookmarked: {},
        bookmark_ids: {}
      };
    }
  }

  /**
   * Rate content
   */
  async rateContent(contentId: string, rating: number, teamId: string, versionNumber?: number): Promise<ContentRatingResponse> {
    try {
      console.log(`Rating content ${contentId}: ${rating} stars`);

      const response = await this.post<any>(`/content/${contentId}/rate`, {
        rating,
        team_id: teamId,
        version_number: versionNumber,
      });
      
      // Invalidate content cache
      this.invalidateCache('content');
      
      return {
        success: true,
        message: response.message || 'Content rated successfully',
        averageRating: response.rating?.newAverageRating ?? response.average_rating ?? response.averageRating,
        totalRatings: response.rating?.totalRatings ?? response.total_ratings ?? response.totalRatings,
        userRating: rating
      };
    } catch (error) {
      console.error(`Error rating content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.rateContent(${contentId})`);
    }
  }

  /**
   * Rate content with comment
   */
  async rateContentWithComment(
    contentId: string,
    rating: number,
    teamId: string,
    comment?: string,
    versionNumber?: number,
  ): Promise<ContentRatingResponse & { commentId?: string }> {
    try {
      console.log(`Rating content ${contentId}: ${rating} stars with comment`);

      const response = await this.post<any>(`/content/${contentId}/rate`, {
        rating,
        comment,
        team_id: teamId,
        version_number: versionNumber,
      });
      
      // Invalidate content cache
      this.invalidateCache('content');
      
      return {
        success: true,
        message: response.message || 'Content rated successfully',
        averageRating: response.rating?.newAverageRating ?? response.average_rating ?? response.averageRating,
        totalRatings: response.rating?.totalRatings ?? response.total_ratings ?? response.totalRatings,
        userRating: rating,
        commentId: response.comment_id || response.commentId
      };
    } catch (error) {
      console.error(`Error rating content ${contentId} with comment:`, error);
      throw this.errorHandler.handleError(error, `ContentService.rateContentWithComment(${contentId})`);
    }
  }

  /**
   * Get content ratings
   */
  async getContentRatings(contentId: string): Promise<{
    average_rating: number;
    total_ratings: number;
    rating_distribution: Record<number, number>;
    recent_ratings?: any[];
  }> {
    try {
      console.log(`Getting ratings for content: ${contentId}`);
      
      const response = await this.getCached<any>(
        `/content/${contentId}/ratings`
      );
      
      return response;
    } catch (error) {
      console.error(`Error getting ratings for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getContentRatings(${contentId})`);
    }
  }

  /**
   * Get content ratings with pagination
   */
  async getContentRatingsPaginated(
    contentId: string,
    params: {
      page?: number;
      limit?: number;
      sort_by?: 'rating' | 'date' | 'helpful';
      sort_order?: 'asc' | 'desc';
      min_rating?: number;
      max_rating?: number;
      version_number?: number;
    }
  ): Promise<{
    data: any[];
    ratings?: any[];
    pagination?: any;
    metadata?: {
      pagination?: any;
    };
  }> {
    try {
      console.log(`Getting paginated ratings for content ${contentId}:`, params);

      // Use requestWithMetadata to preserve the full response structure including metadata
      const response = await this.requestWithMetadata<any[]>(
        'GET',
        `/content/${contentId}/ratings`,
        undefined,
        { params }
      );

      return response;
    } catch (error) {
      console.error(`Error getting paginated ratings for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getContentRatingsPaginated(${contentId})`);
    }
  }

  /**
   * Get user's rating for content
   */
  async getUserContentRating(contentId: string): Promise<any | null> {
    try {
      const response = await this.get<any>(`/content/${contentId}/user-rating`);
      return response;
    } catch (error: any) {
      if (error.status === 404) {
        return null; // User hasn't rated this content
      }
      console.error(`Error getting user rating for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getUserContentRating(${contentId})`);
    }
  }

  /**
   * Convert content to PDF
   * Supports both authenticated users and public link access
   */
  async convertContentToPdf(
    contentId: string,
    teamId: string,
    publicLinkCredentials?: { linkId: string; token: string; password?: string }
  ): Promise<{
    jobId: string;
    status: string;
    message: string;
    immediate?: boolean;
    pdfUrl?: string;
  }> {
    try {
      const isPublicLink = !!publicLinkCredentials;
      console.log(`Converting content ${contentId} to PDF (team: ${teamId}, publicLink: ${isPublicLink})`);

      // Build query params for public link auth if provided
      const queryParams: any = {};
      if (publicLinkCredentials) {
        queryParams.linkId = publicLinkCredentials.linkId;
        queryParams.token = publicLinkCredentials.token;
        if (publicLinkCredentials.password) {
          queryParams.password = publicLinkCredentials.password;
        }
      }

      // Use requestWithMetadata to get the full response structure with message
      // Use 5 minutes timeout for PDF conversion (large files can take time)
      const response = await this.requestWithMetadata<any>('POST', `/content/${contentId}/convert-to-pdf`, {
        team_id: teamId
      }, {
        timeout: 300000, // 5 minutes in milliseconds
        params: Object.keys(queryParams).length > 0 ? queryParams : undefined
      });
      
      // Log the full response to debug the structure
      console.log('PDF conversion response:', response);
      
      const responseData = response.data;
      
      // Handle the response structure from the API
      // The API returns either jobId (for async) or pdfUrl (for immediate)
      // Status is a numeric enum: 1=pending, 2=processing, 3=completed, 4=failed
      let statusString = 'processing';
      if (responseData.status === 1) statusString = 'pending';
      else if (responseData.status === 2) statusString = 'processing';
      else if (responseData.status === 3) statusString = 'completed';
      else if (responseData.status === 4) statusString = 'failed';
      
      return {
        jobId: responseData.jobId || responseData.conversionId || '',
        status: statusString,
        message: response.message || 'Conversion started',
        immediate: responseData.immediate,
        pdfUrl: responseData.pdfUrl || responseData.outputUrl
      };
    } catch (error) {
      console.error(`Error converting content ${contentId} to PDF:`, error);
      throw this.errorHandler.handleError(error, `ContentService.convertContentToPdf(${contentId})`);
    }
  }

  /**
   * Get PDF conversion status
   * Supports both authenticated users and public link access
   */
  async getConversionStatus(
    contentId: string,
    jobId: string,
    publicLinkCredentials?: { linkId: string; token: string; password?: string }
  ): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    pdfUrl?: string;
    error?: string;
  }> {
    try {
      const isPublicLink = !!publicLinkCredentials;
      console.log(`Getting conversion status for content ${contentId}, job ${jobId} (publicLink: ${isPublicLink})`);

      // Build query params for public link auth if provided
      const queryParams: any = {};
      if (publicLinkCredentials) {
        queryParams.linkId = publicLinkCredentials.linkId;
        queryParams.token = publicLinkCredentials.token;
        if (publicLinkCredentials.password) {
          queryParams.password = publicLinkCredentials.password;
        }
      }

      // The get method already extracts the data, so response is the actual data
      const response = await this.get<any>(
        `/content/${contentId}/conversion-status/${jobId}`,
        {
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined
        }
      );
      
      // Convert numeric status to string
      // Status is a numeric enum: 1=pending, 2=processing, 3=completed, 4=failed
      let statusString: 'pending' | 'processing' | 'completed' | 'failed' = 'processing';
      if (response.status === 1 || response.status === 'pending') statusString = 'pending';
      else if (response.status === 2 || response.status === 'processing') statusString = 'processing';
      else if (response.status === 3 || response.status === 'completed') statusString = 'completed';
      else if (response.status === 4 || response.status === 'failed') statusString = 'failed';
      
      return {
        status: statusString,
        progress: response.progress,
        pdfUrl: response.pdf_url || response.pdfUrl,
        error: response.error
      };
    } catch (error) {
      console.error(`Error getting conversion status for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getConversionStatus(${contentId})`);
    }
  }

  /**
   * Create a bookmark for content
   */
  async createBookmark(params: {
    content_id: string;
    folder_id?: string;
    notes?: string;
    tags?: string[];
    organization_id?: string;
    team_id?: string;
    priority?: number;
  }): Promise<{
    success: boolean;
    message: string;
    bookmark_id: string;
  }> {
    try {
      const response = await this.post<any>('/content/bookmarks', params);
      return {
        success: true,
        message: response.message || 'Bookmark created successfully',
        bookmark_id: response.bookmark_id || response.data?.bookmark_id || response.data?.id
      };
    } catch (error) {
      console.error('Error creating bookmark:', error);
      throw this.errorHandler.handleError(error, 'ContentService.createBookmark');
    }
  }

  /**
   * List bookmarks
   */
  async listBookmarks(params: {
    folder_id?: string;
    content_id?: string;
    tags?: string[];
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (params.folder_id) queryParams.append('folder_id', params.folder_id);
      if (params.content_id) queryParams.append('content_id', params.content_id);
      if (params.tags?.length) queryParams.append('tags', params.tags.join(','));
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      return await this.get(`/content/bookmarks?${queryParams.toString()}`);
    } catch (error) {
      console.error('Error listing bookmarks:', error);
      throw this.errorHandler.handleError(error, 'ContentService.listBookmarks');
    }
  }

  /**
   * Delete a bookmark
   */
  async deleteBookmark(bookmarkId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.delete<any>(`/content/bookmarks/${bookmarkId}`);
      return {
        success: true,
        message: response.message || 'Bookmark deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting bookmark ${bookmarkId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.deleteBookmark(${bookmarkId})`);
    }
  }

  // ---- Content Versioning Methods ----

  /**
   * List versions for a content item
   */
  async listVersions(
    contentId: string,
    publisherView?: boolean,
    includeDeleted?: boolean,
    teamId?: string
  ): Promise<any> {
    try {
      const params: any = {};
      if (publisherView !== undefined) params.publisher_view = publisherView;
      if (includeDeleted !== undefined) params.include_deleted = includeDeleted;
      if (teamId) params.team_id = teamId;
      return await this.get<any>(`/content/${contentId}/versions`, { params });
    } catch (error) {
      console.error(`Error listing versions for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.listVersions(${contentId})`);
    }
  }

  /**
   * Create a new draft version for a content item
   */
  async createVersion(contentId: string, params: {
    storageKey?: string;
    fileSizeBytes?: number;
    mimeType?: string;
    thumbnailPath?: string;
    versionLabel?: string;
    changeNotes?: string;
    organizationId: string;
    teamId: string;
    /** Initial status: 'draft'|'private'|'public'|'archived'. Non-draft auto-publishes. */
    status?: string;
    /** If true, AI-suggested title replaces the content title after enrichment. */
    useAiTitle?: boolean;
    /** If true, AI-generated description replaces the content description after enrichment. */
    useAiDescription?: boolean;
    /** Controls AI tag application: 'off' | 'replace' | 'merge'. */
    aiTagMode?: string;
  }): Promise<any> {
    try {
      const body: any = {
        organization_id: params.organizationId,
        team_id: params.teamId,
      };
      if (params.storageKey !== undefined) body.storage_key = params.storageKey;
      if (params.fileSizeBytes !== undefined) body.file_size_bytes = params.fileSizeBytes;
      if (params.mimeType !== undefined) body.mime_type = params.mimeType;
      if (params.thumbnailPath !== undefined) body.thumbnail_path = params.thumbnailPath;
      if (params.versionLabel !== undefined) body.version_label = params.versionLabel;
      if (params.changeNotes !== undefined) body.change_notes = params.changeNotes;
      if (params.status !== undefined) body.status = params.status;
      if (params.useAiTitle !== undefined) body.use_ai_title = params.useAiTitle;
      if (params.useAiDescription !== undefined) body.use_ai_description = params.useAiDescription;
      if (params.aiTagMode !== undefined) body.ai_tag_mode = params.aiTagMode;
      const response = await this.post<any>(`/content/${contentId}/versions`, body);
      this.invalidateCache('content');
      return response;
    } catch (error) {
      console.error(`Error creating version for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.createVersion(${contentId})`);
    }
  }

  /**
   * Get a specific version (versionNumber=0 returns current published)
   */
  async getVersion(contentId: string, versionNumber: number, teamId?: string): Promise<any> {
    try {
      const params = teamId ? { team_id: teamId } : undefined;
      return await this.get<any>(`/content/${contentId}/versions/${versionNumber}`, { params });
    } catch (error) {
      console.error(`Error getting version ${versionNumber} for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getVersion(${contentId}, ${versionNumber})`);
    }
  }

  /**
   * Publish a draft version, making it the active version.
   * targetStatus is required: 'private' | 'public' | 'archived'
   */
  async publishVersion(contentId: string, versionNumber: number, params?: {
    targetStatus?: 'private' | 'public' | 'archived';
    updatedDescription?: string;
    teamId?: string;
  }): Promise<any> {
    try {
      const body: any = { target_status: params?.targetStatus ?? 'public' };
      if (params?.updatedDescription !== undefined) body.updated_description = params.updatedDescription;
      if (params?.teamId) body.team_id = params.teamId;
      const response = await this.post<any>(
        `/content/${contentId}/versions/${versionNumber}/publish`,
        body
      );
      this.invalidateCache('content');
      return response;
    } catch (error) {
      console.error(`Error publishing version ${versionNumber} for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.publishVersion(${contentId}, ${versionNumber})`);
    }
  }

  /**
   * Change the status of a historical (non-draft) version independently.
   * status: 'private' | 'public' | 'archived'
   */
  async setVersionStatus(
    contentId: string,
    versionNumber: number,
    status: 'draft' | 'private' | 'public' | 'archived',
    teamId?: string
  ): Promise<any> {
    try {
      const body: any = { status };
      if (teamId) body.team_id = teamId;
      const response = await this.patch<any>(
        `/content/${contentId}/versions/${versionNumber}/status`,
        body
      );
      this.invalidateCache('content');
      return response;
    } catch (error) {
      console.error(`Error setting status for version ${versionNumber} of content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.setVersionStatus(${contentId}, ${versionNumber})`);
    }
  }

  async setActiveVersion(contentId: string, versionNumber: number, teamId?: string): Promise<any> {
    try {
      const body: any = {};
      if (teamId) body.team_id = teamId;
      const response = await this.post<any>(
        `/content/${contentId}/versions/${versionNumber}/activate`,
        body
      );
      this.invalidateCache('content');
      return response;
    } catch (error) {
      console.error(`Error activating version ${versionNumber} of content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.setActiveVersion(${contentId}, ${versionNumber})`);
    }
  }

  /**
   * Permanently delete a version
   */
  async deleteVersion(contentId: string, versionNumber: number, teamId?: string): Promise<any> {
    try {
      const params: any = teamId ? { team_id: teamId } : undefined;
      const response = await this.delete<any>(
        `/content/${contentId}/versions/${versionNumber}`,
        { params }
      );
      this.invalidateCache('content');
      this.invalidateCache('team');
      this.invalidateCache('subscription');
      return response;
    } catch (error) {
      console.error(`Error deleting version ${versionNumber} for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.deleteVersion(${contentId}, ${versionNumber})`);
    }
  }

  /**
   * Get version stats (versionNumber=0 or omit for aggregate across all versions)
   */
  async getVersionStats(contentId: string, versionNumber?: number, teamId?: string): Promise<any> {
    try {
      const params: any = {};
      if (versionNumber !== undefined) params.version_number = versionNumber;
      if (teamId) params.team_id = teamId;
      return await this.get<any>(`/content/${contentId}/versions/stats`, {
        params: Object.keys(params).length > 0 ? params : undefined
      });
    } catch (error) {
      console.error(`Error getting version stats for content ${contentId}:`, error);
      throw this.errorHandler.handleError(error, `ContentService.getVersionStats(${contentId})`);
    }
  }

  /**
   * Regenerate only the thumbnail for a content item.
   *
   * Distinct from retryProcessing, which also resets the AI auto-promotion
   * settings so fresh suggestions can be reviewed. Someone fixing a missing
   * preview image has not asked for that.
   */
  async regenerateThumbnail(
    contentId: string,
    teamId: string,
  ): Promise<{ queued: number; state?: string; message: string }> {
    try {
      const response = await this.post<any>(`/content/${contentId}/regenerate-thumbnail`, { team_id: teamId });
      this.invalidateCache('content');
      return response;
    } catch (error) {
      throw this.errorHandler.handleError(error, `ContentService.regenerateThumbnail(${contentId})`);
    }
  }

  /**
   * Re-queue a stuck content item for AI processing
   */
  async retryProcessing(contentId: string, teamId: string): Promise<{ queued: number; message: string }> {
    try {
      const response = await this.post<any>(`/content/${contentId}/retry-processing`, { team_id: teamId });
      this.invalidateCache('content');
      return response;
    } catch (error) {
      throw this.errorHandler.handleError(error, `ContentService.retryProcessing(${contentId})`);
    }
  }

  /**
   * Get content streaming URL
   */
  getContentStreamingUrl(contentId: string): string {
    // Generate full streaming URL with API prefix for direct media element usage
    // Note: This URL is used directly in <audio> and <video> src attributes
    // Include authentication token as query parameter since media elements don't use axios interceptors
    const token = this.authInterceptor.getAccessToken();
    const baseUrl = `/api/v2/content/${contentId}/stream`;
    
    if (token) {
      // Add token as query parameter for authentication
      return `${baseUrl}?token=${encodeURIComponent(token)}`;
    }
    
    return baseUrl;
  }
}

// Export singleton instance
export const contentService = new ContentService();